/**
 * What to do when a photo is scanned AFTER the player has already typed in their hand (Indra, 2026-10-05): "when in doubt, use the one the
 * player input". Until now the scan simply replaced whatever was entered. A photo is a good second pair of eyes, not a better one, so:
 *
 *  - where the photo was NOT sure about a tile (recognition flagged it) and the player's own tiles disagree, the player's tile is kept;
 *  - where the photo read something that is not a complete hand at all and the player's entry is, the player's whole hand is kept (the
 *    photo stays attached as a reference);
 *  - where the photo is confident, the hand is complete and it differs from the entry, the photo's reading is shown with a note to check it
 *    (it is not doubt, and the engine accepts it);
 *  - what only the player can know (declared kongs, the winning tile, a claimed set, flowers) is always kept.
 *
 * "Complete" is the scoring engine's own judgement (`isCompleteHand`) — the same one the live preview uses — so a hand either side agrees
 * on is chosen by the rules rather than by a guess. Pure: no I/O, no DOM.
 */

import { getRuleset } from '../rules';
import { isTile, rankOf, suitOf, type BonusTile, type Tile } from '../rules/tiles';
import type { Meld } from '../rules/types';

export interface ScanHand {
  concealed: Tile[];
  melds: Meld[];
  flowers: BonusTile[];
  /** Indices into `concealed` that recognition was not confident about. */
  flaggedIndices: number[];
}

export interface EnteredHand {
  concealed: Tile[];
  melds: Meld[];
  winningTile?: Tile;
  flowers: BonusTile[];
  claimedSet?: boolean;
}

export interface MergedHand {
  concealed: Tile[];
  melds: Meld[];
  flowers: BonusTile[];
  winningTile?: Tile;
  claimedSet?: boolean;
  /** Tiles the photo was unsure about and nobody has settled — the "double-check against the photo" callout. */
  stillFlagged: Tile[];
  /** One plain sentence on what was done, when something was. */
  note?: string;
}

export type CompleteCheck = (concealed: Tile[], melds: Meld[]) => boolean;

const hkosComplete: CompleteCheck = (concealed, melds) =>
  getRuleset('hkos').isCompleteHand({ concealed, melds, winningTile: concealed[0] ?? '1m' });

/** The leftover entered tile that most likely stands for a misread one: the same suit, the nearest rank; otherwise simply the first. */
function closestLeftover(leftover: Tile[], misread: Tile): number {
  if (!isTile(misread)) return leftover.length > 0 ? 0 : -1;
  let best = -1;
  let bestDistance = Infinity;
  leftover.forEach((t, i) => {
    if (!isTile(t) || suitOf(t) !== suitOf(misread)) return;
    const distance = suitOf(t) === 'z' ? (t === misread ? 0 : 1) : Math.abs(rankOf(t) - rankOf(misread));
    if (distance < bestDistance) {
      best = i;
      bestDistance = distance;
    }
  });
  return best >= 0 ? best : leftover.length > 0 ? 0 : -1;
}

const count = (tiles: Tile[], tile: Tile): number => tiles.filter((t) => t === tile).length;

export function mergeScanWithEntered(scan: ScanHand, entered: EnteredHand | undefined, isComplete: CompleteCheck = hkosComplete): MergedHand {
  const flagged = new Set(scan.flaggedIndices);
  const scanFlat = [...scan.concealed, ...scan.melds.flatMap((m) => m.tiles)];
  const asScanned = (): MergedHand => ({
    concealed: scanFlat,
    melds: [],
    flowers: entered && entered.flowers.length > 0 ? entered.flowers : scan.flowers,
    ...(entered?.claimedSet ? { claimedSet: true } : {}),
    stillFlagged: [...new Set(scan.flaggedIndices.map((i) => scan.concealed[i]).filter((t): t is Tile => t !== undefined && isTile(t)))],
  });

  // Nothing typed in yet: the photo's reading stands, as it always did.
  if (!entered || entered.concealed.length + entered.melds.length === 0) return asScanned();

  const keptMelds = entered.melds;
  const kongCount = keptMelds.filter((m) => m.type === 'kong').length;
  const target = 14 - 3 * kongCount;

  // The photo shows the declared kongs too; the player already said what they are, so take those tiles out of the photo's pile.
  let items = scan.concealed.map((tile, i) => ({ tile, flagged: flagged.has(i), matched: false, replaced: false }));
  for (const k of keptMelds) {
    const type = k.tiles[0];
    if (type === undefined) continue;
    let toRemove = k.tiles.length;
    items = items.filter((it) => (toRemove > 0 && it.tile === type ? (toRemove--, false) : true));
  }

  // Match the photo's tiles with the player's, tile for tile. What is left on each side is where they disagree.
  // The photo's CONFIDENT tiles are matched first: when it shows two identical tiles and only one of them was flagged, it is the flagged
  // one that is left unmatched — the one that may have been misread — not whichever happened to come first.
  const pool = [...entered.concealed];
  for (const sureFirst of [false, true]) {
    for (const it of items) {
      if (it.matched || it.flagged !== sureFirst) continue;
      const at = pool.indexOf(it.tile);
      if (at >= 0) {
        it.matched = true;
        pool.splice(at, 1);
      }
    }
  }
  const leftover = pool; // entered tiles the photo did not show

  const unsureAndDisagreeing = items.filter((it) => !it.matched && it.flagged);
  const scanAlone = items.map((it) => it.tile);
  const doubt = unsureAndDisagreeing.length > 0 || scanAlone.length !== target || !isComplete(scanAlone, keptMelds);

  const flowers = entered.flowers.length > 0 ? entered.flowers : scan.flowers;
  const finish = (concealed: Tile[], extra: Partial<MergedHand>): MergedHand => {
    const base: MergedHand = {
      concealed,
      melds: keptMelds,
      flowers,
      stillFlagged: [],
      ...(entered.claimedSet ? { claimedSet: true } : {}),
      ...(entered.winningTile && count(concealed, entered.winningTile) > 0 ? { winningTile: entered.winningTile } : {}),
    };
    return { ...base, ...extra };
  };
  const stillFlaggedOf = (list: typeof items): Tile[] => [...new Set(list.filter((it) => it.flagged && !it.matched && !it.replaced).map((it) => it.tile).filter(isTile))];

  if (!doubt) {
    const differs = items.some((it) => !it.matched) || leftover.length > 0;
    return finish(scanAlone, {
      stillFlagged: stillFlaggedOf(items),
      ...(differs ? { note: 'The photo read a different hand from the one you entered. Please check it against the photo.' } : {}),
    });
  }

  // In doubt: a tile the photo was unsure about becomes the player's own tile.
  const spare = [...leftover];
  let replaced = 0;
  for (const it of items) {
    if (it.matched || !it.flagged) continue;
    const at = closestLeftover(spare, it.tile);
    if (at < 0) continue;
    it.tile = spare.splice(at, 1)[0]!;
    it.replaced = true;
    replaced++;
  }
  // A short photo reading is made up to a hand with the player's remaining tiles.
  const merged = items.map((it) => it.tile);
  while (merged.length < target && spare.length > 0) merged.push(spare.shift()!);

  const replacedNote =
    replaced > 0
      ? `The photo wasn't sure about ${replaced} tile${replaced === 1 ? '' : 's'}, so ${replaced === 1 ? 'the tile' : 'the tiles'} you entered ${replaced === 1 ? 'was' : 'were'} kept there.`
      : undefined;

  if (isComplete(merged, keptMelds)) {
    return finish(merged, { stillFlagged: stillFlaggedOf(items), ...(replacedNote ? { note: replacedNote } : {}) });
  }
  if (isComplete(entered.concealed, keptMelds)) {
    return finish([...entered.concealed], {
      note: "The photo didn't read as a complete hand, so the hand you entered was kept. The photo is attached as a reference.",
    });
  }
  return finish(merged, { stillFlagged: stillFlaggedOf(items), ...(replacedNote ? { note: replacedNote } : {}) });
}
